import React, { useState, useEffect } from 'react';
import {
  Box,
  IconButton,
  Typography,
  TextField,
  InputAdornment,
  Dialog,
  DialogActions,
  DialogContent
} from '@mui/material';
import { StaticDatePicker } from '@mui/x-date-pickers/StaticDatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import ptBr from "date-fns/locale/pt-BR";
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { FiChevronLeft, FiChevronRight, FiSearch, FiCalendar } from 'react-icons/fi';
import { format } from 'date-fns';
import { DateNavContainer } from '../../styles';
import { FutButton } from '@/components/FutButton';
import styled, { keyframes } from 'styled-components';

// Animações
const pulse = keyframes`
  0% {
    box-shadow: 0 0 5px rgba(42, 244, 151, 0.3);
  }
  50% {
    box-shadow: 0 0 7px rgba(42, 244, 151, 0.6);
  }
  100% {
    box-shadow: 0 0 5px rgba(42, 244, 151, 0.3);
  }
`;

const borderPulse = keyframes`
  0% {
    border-color: rgba(42, 244, 151, 0.3);
  }
  50% {
    border-color: rgba(42, 244, 151, 0.8);
  }
  100% {
    border-color: rgba(42, 244, 151, 0.3);
  }
`;

// Componentes estilizados
const NeonDateNavContainer = styled(Box)`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  margin-bottom: 24px;
  border-radius: 8px;
  background: rgba(20, 20, 25, 0.7);
  border: 1px solid rgba(42, 244, 151, 0.3);
  box-shadow: 0 0 15px rgba(42, 244, 151, 0.2);
  backdrop-filter: blur(5px);
  animation: ${pulse} 4s infinite ease-in-out;
`;

const NeonIconButton = styled(IconButton)`
  color: rgba(42, 244, 151, 0.9);
  transition: all 0.3s ease;
  
  &:hover {
    color: rgba(42, 244, 151, 1);
    transform: scale(1.1);
    box-shadow: 0 0 15px rgba(42, 244, 151, 0.5);
  }
`;

const DateDisplay = styled(Box)`
  display: flex;
  align-items: center;
  cursor: pointer;
  margin: 0 8px;
  padding: 4px 12px;
  border-radius: 4px;
  border: 1px solid rgba(42, 244, 151, 0.3);
  transition: all 0.3s ease;
  animation: ${borderPulse} 3s infinite ease-in-out;
  
  &:hover {
    background-color: rgba(42, 244, 151, 0.1);
    transform: scale(1.05);
  }
`;

const NeonTypography = styled(Typography)`
  color: rgba(42, 244, 151, 0.9);
  text-shadow: 0 0 5px rgba(42, 244, 151, 0.5);
  font-weight: 500;
  letter-spacing: 0.5px;
`;

const NeonTextField = styled(TextField)`
  .MuiOutlinedInput-root {
    transition: all 0.3s ease;
    border-radius: 4px;
    
    fieldset {
      border-color: rgba(42, 244, 151, 0.3);
    }
    
    &:hover fieldset {
      border-color: rgba(42, 244, 151, 0.6);
    }
    
    &.Mui-focused fieldset {
      border-color: rgba(42, 244, 151, 0.8);
      box-shadow: 0 0 10px rgba(42, 244, 151, 0.3);
    }
  }
  
  .MuiInputBase-input {
    color: rgba(255, 255, 255, 0.9);
  }
  
  .MuiInputAdornment-root {
    color: rgba(42, 244, 151, 0.8);
  }
`;

const NeonDialogContent = styled(DialogContent)`
  background-color: rgba(20, 20, 25, 0.95);
  border: 1px solid rgba(42, 244, 151, 0.3);
  box-shadow: 0 0 30px rgba(42, 244, 151, 0.4);
  
  .MuiPickersDay-daySelected {
    background-color: rgba(42, 244, 151, 0.8) !important;
  }
  
  .MuiPickersDay-day:hover {
    background-color: rgba(42, 244, 151, 0.2) !important;
  }
  
  .MuiPickersDay-today {
    border-color: rgba(42, 244, 151, 0.8) !important;
  }
`;

const NeonDialogActions = styled(DialogActions)`
  background-color: rgba(20, 20, 25, 0.95);
  border-top: 1px solid rgba(42, 244, 151, 0.3);
`;

interface DateNavigationProps {
  date: Date;
  searchQuery: string;
  onDateChange: (direction: 'prev' | 'next') => void;
  onSearchChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onSpecificDateChange?: (date: Date) => void;
}

const DateNavigation: React.FC<DateNavigationProps> = ({
  date,
  searchQuery,
  onDateChange,
  onSearchChange,
  onSpecificDateChange
}) => {
  const [openDatePicker, setOpenDatePicker] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Date | null>(date);
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState(searchQuery);
  const [searchInputValue, setSearchInputValue] = useState(searchQuery);

  // Update selectedDate whenever the date prop changes
  useEffect(() => {
    setSelectedDate(date);
  }, [date]);

  const handleOpenDatePicker = () => {
    setOpenDatePicker(true);
  };

  const handleCloseDatePicker = () => {
    setOpenDatePicker(false);
  };
  
  // Função para aceitar a data selecionada
  const handleAccept = (newDate: Date | null) => {
    setSelectedDate(newDate);
    if (newDate && onSpecificDateChange) {
      onSpecificDateChange(newDate);
      handleCloseDatePicker();
    }
  };

  // Função para cancelar a seleção de data
  const handleCancel = () => {
    setSelectedDate(date);
    handleCloseDatePicker();
  };

  // Controla a entrada do usuário imediatamente para feedback visual
  const handleSearchInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchInputValue(event.target.value);
  };

  // Aplica debounce para a pesquisa
  useEffect(() => {
    const timer = setTimeout(() => {
      if (debouncedSearchQuery !== searchInputValue) {
        setDebouncedSearchQuery(searchInputValue);
        // Cria um evento fictício para manter a compatibilidade com a interface atual
        const syntheticEvent = {
          target: { value: searchInputValue }
        } as React.ChangeEvent<HTMLInputElement>;
        onSearchChange(syntheticEvent);
      }
    }, 300);

    return () => {
      clearTimeout(timer);
    };
  }, [searchInputValue, debouncedSearchQuery, onSearchChange]);

  return (
    <Box 
      sx={{ 
        position: 'sticky', 
        top: 65, 
        zIndex: 1100,
      }}
    >
      <NeonDateNavContainer>
        <Box display="flex" alignItems="center">
          <NeonIconButton onClick={() => onDateChange('prev')} aria-label="Dia anterior">
            <FiChevronLeft />
          </NeonIconButton>
          
          <DateDisplay onClick={handleOpenDatePicker}>
            <NeonTypography variant="body2" sx={{ mr: 0.5, lineHeight: "normal" }}>
              {format(date, 'dd/MM')}
            </NeonTypography>
            <FiCalendar size={14} color="rgba(42, 244, 151, 0.9)" />
          </DateDisplay>
          
          <NeonIconButton onClick={() => onDateChange('next')} aria-label="Próximo dia">
            <FiChevronRight />
          </NeonIconButton>
        </Box>

        <NeonTextField
          placeholder="Pesquisar partidas..."
          size="small"
          value={searchInputValue}
          onChange={handleSearchInputChange}
          sx={{ width: { xs: '100%', sm: '250px' }, ml: { xs: 0, sm: 2 } }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <FiSearch fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
        
        <Dialog 
          open={openDatePicker} 
          onClose={handleCloseDatePicker}
          PaperProps={{
            sx: { 
              overflowY: 'visible',
              maxWidth: 'fit-content',
              background: 'rgba(20, 20, 25, 0.95)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(42, 244, 151, 0.3)',
              boxShadow: '0 0 30px rgba(42, 244, 151, 0.4)',
            }
          }}
        >
          <NeonDialogContent sx={{ p: 0, overflowY: 'visible' }}>
            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ptBr}>
              <StaticDatePicker
                displayStaticWrapperAs="desktop"
                value={selectedDate}
                onAccept={handleAccept}
                sx={{
                  '& .MuiPickersDay-root': {
                    color: '#fff',
                    fontWeight: 400,
                    '&.Mui-selected': {
                      backgroundColor: 'rgba(42, 244, 151, 0.8)',
                      color: '#000',
                      fontWeight: 'bold',
                      boxShadow: '0 0 10px rgba(42, 244, 151, 0.5)',
                    },
                    '&:hover': {
                      backgroundColor: 'rgba(42, 244, 151, 0.2)',
                    },
                  },
                  '& .MuiPickersDay-today': {
                    border: '1px solid rgba(42, 244, 151, 0.8)',
                  },
                  '& .MuiPickersCalendarHeader-label': {
                    color: 'rgba(42, 244, 151, 0.9)',
                  },
                  '& .MuiSvgIcon-root': {
                    color: 'rgba(42, 244, 151, 0.9)',
                  },
                  '& .MuiTypography-root': {
                    color: '#fff',
                  },
                }}
              />
            </LocalizationProvider>
          </NeonDialogContent>
          <NeonDialogActions sx={{ justifyContent: 'flex-end', px: 2, pb: 2 }}>
            <FutButton 
              onClick={handleCancel} 
              variant="text"
              sx={{
                color: 'rgba(42, 244, 151, 0.9)',
                '&:hover': {
                  backgroundColor: 'rgba(42, 244, 151, 0.1)',
                }
              }}
            >
              Cancelar
            </FutButton>
          </NeonDialogActions>
        </Dialog>
      </NeonDateNavContainer>
    </Box>
  );
};

export default DateNavigation;